import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, computed, effect, linkedSignal, signal, untracked } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { DashLangService } from "../../../../../../modules/home/dash/service/dash.lang.service";
import { DashPanelService } from "../../../../../../modules/home/dash/service/dash.panel.service";
import { S25CheckboxModule } from "../../../../../../modules/s25-checkbox/s25.checkbox.module";
import { S25DatepickerModule } from "../../../../../../modules/s25-datepicker/s25.datepicker.module";
import { S25DropdownPaginatedModule } from "../../../../../../modules/s25-dropdown/s25.dropdown.paginated.module";
import { S25EditableModule } from "../../../../../../modules/s25-editable/s25.editable.module";
import { S25LoadingInlineStaticComponent } from "../../../../../../modules/s25-loading/s25.loading.inline.static.component";
import { EventFormDatePatternPickerModal } from "../../../../../../modules/modal/modals/event.form.date.pattern.picker.modal.component";
import { ModalService } from "../../../../../../modules/modal/modal.service";
import { S25SimpleCollapseModule } from "../../../../../../modules/s25-simple-collapse/s25.simple.collapse.module";
import { S25ToggleButtonModule } from "../../../../../../modules/s25-toggle-button/s25.toggle.button.module";
import { S25TriggerRerenderModule } from "../../../../../../modules/s25-trigger-rerender/s25.trigger.rerender.module";
import { TypeManagerDecorator } from "../../../../../../main/type.map.service";
import { AccessLevels } from "../../../../../../pojo/Fls";
import { Item } from "../../../../../../pojo/Item";
import { DropDownItem } from "../../../../../../pojo/DropDownItem";
import { Event } from "../../../../../../pojo/Event";
import { FormModel } from "../../../../../../pojo/FormI";
import { ProfileApiI, ProfileI } from "../../../../../../pojo/ProfileI";
import { TimeModelI } from "../../../../../../pojo/TimeModelI";
import { EventFormOccurrenceUtil } from "../../../../../../modules/s25-event-creation-form/occurrences/event.form.occurrence.util";
import { ProfileUtil } from "../../../../../../modules/s25-datepattern/profile.util";
import { OccurrenceProfileUtil } from "../../../../../../util/occurrence.profile.util";
import { S25Datefilter } from "../../../../../../modules/s25-dateformat/s25.datefilter.service";
import { DateTemplateRecord, DateTemplateService } from "../../../../../../services/date.template.service";
import { DatetimeService } from "../../../../../../services/datetime.service";
import { FlsService } from "../../../../../../services/fls.service";
import { SpaceService } from "../../../../../../services/space.service";
import { SearchService } from "../../../../../../services/search/search.service";
import { StandardScheduleService } from "../../../../../../services/standard.schedule.service";
import { StateService } from "../../../../../../services/state.service";
import { UserprefService } from "../../../../../../services/userpref.service";
import { TelemetryService } from "../../../../../../services/telemetry.service";
import { s25Resource } from "../../../../../../util/s25.resource";
import { S25Util } from "../../../../../../util/s25-util";
import {
    additionalTimeLabel,
    ADDITIONAL_TIME_TYPES,
    ADDITIONAL_TIME_UNIT_MAX,
    ADDITIONAL_TIME_UNITS,
    createDefaultTimeModel,
    parseDateTemplate,
    refreshAdditionalTimeFieldsFromMinutes,
    resolveThroughDate,
    seedTemplateOccurrence,
    syncAdditionalTimeMinutesFromFields,
    syncTemplateOccurrencesFromProfileCode,
} from "./find-location.date-template.util";
import { HomeDashAvailableSpaceFindLocationResultsComponent } from "./home.dash.available.space.find.location.results.component";
import { DashMeetingPatternApiBean } from "../../../../../../modules/s25-standard-schedule/s25.dash.meeting.pattern.picker.component";
import { S25StandardScheduleModule } from "../../../../../../modules/s25-standard-schedule/s25.standard.schedule.module";
import {
    AdditionalTimeType,
    AdditionalTimeUnit,
    FindLocationLang,
    FindLocationOccurrence,
    FindLocationResultsQuery,
    SpaceAvailabilityItem,
} from "./find-location.types";
import Occurrence = Event.Occurrence;

/** Find a location/space for a chosen date and time (available-space panel path 4001). */
@TypeManagerDecorator("s25-ng-dash-available-space-find-location")
@Component({
    selector: "s25-ng-dash-available-space-find-location",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: "s25-ng-dash-available-space-find-location" },
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        p {
            margin: 0;
            line-height: 1.45;
        }

        label {
            font-weight: bold;
        }

        .find-location-date-mode {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.75rem;
        }

        /*
         * The shared toggle CSS positions the second label with a large left padding
         * (padding-left: 2em) meant to be pulled back under the knob; that pullback does
         * not land cleanly in this panel, so neutralize it (matches the original ANG-5867 fix).
         */
        .find-location-date-mode ::ng-deep .toggle__label--2 {
            margin-left: 0.2em;
            padding-left: 0.2em;
        }

        .find-location-past-date-warning {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
        }

        .template-toggles {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
            max-width: 24rem;
        }

        .template-toggles s25-generic-dropdown {
            display: block;
            width: 100%;
        }

        .template-toggles ::ng-deep .ngDropdownPaginated,
        .template-toggles ::ng-deep .select2-container {
            width: 100%;
        }

        .template-extras {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            padding-top: 0.75rem;
            border-top: 1px solid rgba(0, 0, 0, 0.08);
        }

        .duration-summary {
            display: flex;
            flex-direction: column;
            align-items: center;
            width: 210px;
            padding: 0.8em 0;
            text-align: center;
            border: 1px solid var(--color--border);
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .duration-summary__value {
            font-weight: bold;
        }

        s25-ng-editable-start-end-datetime {
            align-self: flex-start;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--wrapper {
            display: flex;
            flex-direction: column;
            align-content: flex-start;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.35rem 0.5rem;
            max-width: none;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date,
        s25-ng-editable-start-end-datetime ::ng-deep .timepicker.ngInlineBlock {
            width: 10.5rem;
            max-width: 10.5rem;
            vertical-align: middle;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date .s25-datepicker,
        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date .form-group,
        s25-ng-editable-start-end-datetime ::ng-deep .timepicker .ngTimepicker {
            width: 100%;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date input.c-input,
        s25-ng-editable-start-end-datetime ::ng-deep .ngTimepickerInput.cn-form__control {
            box-sizing: border-box;
            width: 100% !important;
            max-width: 100% !important;
            height: 2em;
            min-height: 2em;
            margin: 0;
            padding: 0 0.75em !important;
            line-height: 2em;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .start-end-date-title {
            margin: 0;
            padding: 0;
            line-height: 2em;
        }

        .attendees-row {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.5rem;
        }

        .attendees-input {
            width: 8rem;
        }

        #My25Live_EventCreation_Attendees {
            width: 8rem;
        }

        .required-hint {
            font-size: 0.9em;
            color: var(--color--text-muted);
        }

        s25-ng-search-dropdown {
            display: block;
        }

        .extra-time-type {
            padding: 0.75rem;
        }

        .extra-time-type + .extra-time-type {
            margin-top: 0.5rem;
        }

        .extra-time-label {
            font-weight: bold;
            margin-bottom: 0.35rem;
        }

        .extra-time-units {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem 1.25rem;
        }

        .extra-time-unit {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            margin: 0;
            font-weight: normal;
        }

        .reservation-summary {
            display: grid;
            gap: 0.5rem;
            margin-top: 0.75rem;
            padding: 0.75rem;
            border-radius: 4px;
            background: var(--color--surface-2-bg);
        }

        .calendar {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .profile-save {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        .profile-actions {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem 0.75rem;
        }
    `,
    imports: [
        CommonModule,
        FormsModule,
        S25DropdownPaginatedModule,
        S25EditableModule,
        S25DatepickerModule,
        S25SimpleCollapseModule,
        S25CheckboxModule,
        S25ToggleButtonModule,
        S25TriggerRerenderModule,
        S25LoadingInlineStaticComponent,
        HomeDashAvailableSpaceFindLocationResultsComponent,
        S25StandardScheduleModule,
    ],
    template: `
        @if (findLocationLang.value(); as context) {
            <p>{{ context.text.enter_the_desired_date }}</p>

            @if (showDateModeToggle()) {
                <div class="find-location-date-mode">
                    <s25-toggle-button
                        [value]="dateModeToggleValue()"
                        (valueChange)="onDateModeToggleChange($event)"
                        falseLabel="Date template"
                        trueLabel="Meeting pattern"
                        toggleLabel="Advanced date selection"
                    />
                    @if (dateMode() !== "simple") {
                        <button
                            type="button"
                            class="aw-button aw-button--outline find-location-date-mode-reset"
                            tabindex="0"
                            (click)="resetDateMode()"
                        >
                            Reset
                        </button>
                    }
                </div>
            }

            @if (useDateTemplate()) {
                <div class="template-toggles">
                    <s25-ng-checkbox
                        [value]="showExtraFields()"
                        (valueChange)="onShowExtraFieldsChange($event)"
                        labelId="showextrafields"
                    >
                        Show extra fields
                    </s25-ng-checkbox>

                    <s25-generic-dropdown
                        *s25-ng-trigger-rerender="dateTemplateDropdownRemountKey()"
                        [items]="dateTemplateDropdownItems()"
                        [placeholder]="'Search templates'"
                        [value]="selectedDateTemplateItem()"
                        (valueChange)="onDateTemplateSelect($event)"
                    />
                </div>
            }

            @if (useMeetingPattern()) {
                <s25-ng-dash-meeting-pattern-picker [apiBean]="meetingPatternApiBean" />
            }

            @if (datetimeReady() && dateMode() !== "meetingPattern") {
                <s25-ng-editable-start-end-datetime
                    *s25-ng-trigger-rerender="datetimeRemountKey()"
                    [startDatetime]="startDatetime()!"
                    [endDatetime]="endDatetime()!"
                    [noEndDate]="true"
                    [noSpansMidnightEdit]="true"
                    [noTimeUpdated]="true"
                    [alwaysEditing]="true"
                    (startDatetimeChange)="onStartDatetimeChange($event)"
                    (endDatetimeChange)="onEndDatetimeChange($event)"
                />
            }

            @if (pastDateWarning(); as pastWarning) {
                <div class="ngBold ngRed rose-form--error-text find-location-past-date-warning" tabindex="0">
                    {{ pastWarning }}
                    <button type="button" class="aw-button aw-button--outline" (click)="ignorePastDateWarning()">
                        Ignore
                    </button>
                </div>
            }

            @if (showTemplateExtras(); as timeModel) {
                <div class="template-extras ngDateTemplate">
                    <div class="duration-summary">
                        <span>Duration:</span>
                        <span class="duration-summary__value">{{ eventDuration() }}</span>
                    </div>

                    <s25-simple-collapse
                        header="Additional Time"
                        [expanded]="additionalTimeExpanded()"
                        (expandedChange)="additionalTimeExpanded.set($event)"
                    >
                        @for (timeType of additionalTimeTypes; track timeType) {
                            <div class="extra-time-type">
                                <div class="extra-time-label">{{ additionalTimeLabel(timeType) }}</div>
                                <div class="extra-time-units">
                                    @for (unit of additionalTimeUnits; track unit) {
                                        <label class="extra-time-unit" [attr.aria-label]="unit">
                                            <s25-ng-editable-number
                                                class="rose-form--additional-time"
                                                [val]="additionalTimeField(timeModel, timeType, unit)"
                                                [min]="0"
                                                [max]="additionalTimeUnitMax[unit]"
                                                [ariaLabel]="
                                                    additionalTimeField(timeModel, timeType, unit) +
                                                    ' ' +
                                                    unit +
                                                    ', edit ' +
                                                    additionalTimeLabel(timeType) +
                                                    ' ' +
                                                    unit.toLowerCase()
                                                "
                                                (valChange)="onAdditionalTimeFieldChange(timeType, unit, $event)"
                                            />
                                            <span>{{ unit }}</span>
                                        </label>
                                    }
                                </div>
                            </div>
                        }

                        <div class="reservation-summary">
                            <div>
                                Reservation Start:
                                <span class="ngBold">{{ reservationStartLabel() }}</span>
                            </div>
                            <div>
                                Reservation End:
                                <span class="ngBold">{{ reservationEndLabel() }}</span>
                            </div>
                            <div class="reservation-duration">
                                Total Reservation Duration:
                                <span class="ngBold">{{ reservationDuration() }}</span>
                            </div>
                        </div>
                    </s25-simple-collapse>

                    <div class="calendar">
                        <p>
                            Click on the calendar below to add dates or click the
                            <strong>Repeating Pattern</strong> button to select a date pattern.
                        </p>

                        <button
                            type="button"
                            class="aw-button aw-button--primary patternButton"
                            (click)="openRepeatingPatternModal()"
                        >
                            Repeating Pattern
                        </button>

                        <s25-ng-occurrence-datepicker
                            [firstDate]="timeModel.evStartDt"
                            [occurrences]="templateOccurrences()"
                            [includeFirstDate]="true"
                            [timeModel]="timeModel"
                            (occurrencesChange)="onCalendarOccurrencesChange($event)"
                        />
                    </div>

                    @if (hasDateTemplateSave()) {
                        <div class="profile-save ngDateTemplateSave">
                            @if (dateTemplateSaving()) {
                                <s25-ng-loading-inline-static />
                            }
                            <label for="find-location-profile-name">Profile Name</label>
                            <input
                                id="find-location-profile-name"
                                type="text"
                                class="c-input"
                                [ngModel]="profileName()"
                                (ngModelChange)="profileName.set($event)"
                            />
                            <div class="profile-actions">
                                <button type="button" class="c-textButton" (click)="resetDateTemplate()">Reset</button>
                                <button
                                    type="button"
                                    class="c-textButton"
                                    [disabled]="dateTemplateSaving()"
                                    (click)="saveDateTemplate(true)"
                                >
                                    Save As
                                </button>
                                <button
                                    type="button"
                                    class="aw-button aw-button--primary"
                                    [disabled]="dateTemplateSaving()"
                                    (click)="saveDateTemplate(false)"
                                >
                                    Save
                                </button>
                                @if (selectedDateTemplateId()) {
                                    <button
                                        type="button"
                                        class="aw-button aw-button--danger--outline"
                                        [disabled]="dateTemplateSaving()"
                                        (click)="deleteDateTemplate()"
                                    >
                                        Delete
                                    </button>
                                }
                            </div>
                        </div>
                    }
                </div>
            }

            <label [attr.for]="context.style.attendees">
                {{ context.text.number_of_attendees }}
                <span class="required-hint" [attr.id]="attendeesRequiredHintId">{{ context.text.required }}</span>
            </label>
            <div class="attendees-row">
                <input
                    [attr.id]="context.style.attendees"
                    class="attendees-input c-input"
                    type="text"
                    tabindex="0"
                    required
                    aria-required="true"
                    [attr.aria-describedby]="attendeesRequiredHintId"
                    [ngModel]="numberOfAttendees()"
                    (ngModelChange)="numberOfAttendees.set($event)"
                    (keydown.enter)="showLocations()"
                />
            </div>

            <label [attr.id]="searchWithinLabelId">
                {{ context.text.search_within }}
                <span class="required-hint">{{ context.text.required }}</span>
            </label>
            <s25-ng-search-dropdown
                [attr.id]="context.style.search_within"
                [itemTypeId]="Item.Ids.Location"
                [allowNonQueryId]="false"
                [placeholder]="locationSearchPlaceholder()"
                [ariaLabel]="searchWithinAriaLabel()"
                [additionalSearches]="additionalLocationSearches()"
                [value]="selectedLocationSearch()"
                (valueChange)="onLocationSearchChange($event)"
            />

            <button
                type="button"
                [attr.id]="context.button.style.id"
                class="aw-button aw-button--primary"
                tabindex="0"
                (click)="showLocations()"
            >
                {{ context.text.show_locations_prompt }}
            </button>

            @if (showResults()) {
                @if (resultsContext(); as resultsLang) {
                    <s25-ng-dash-available-space-find-location-results
                        [lang]="resultsLang"
                        [items]="availabilityItems()"
                        [loaded]="availabilityLoaded()"
                        [loading]="availabilityResource.isLoading()"
                        [hasPrevCapacity]="hasPrevCapacity()"
                        [hasNextCapacity]="hasNextCapacity()"
                        [descriptionHtml]="resultsDescriptionHtml()"
                        (viewLocation)="viewLocation($event)"
                        (useSpace)="useSpace($event)"
                        (prevCapacity)="loadPrevCapacity()"
                        (nextCapacity)="loadNextCapacity()"
                        (advancedSearch)="advancedSearch()"
                    />
                }
            }
        }
    `,
})
export class HomeDashAvailableSpaceFindLocationComponent {
    readonly Item = Item;
    readonly datePatternApi: ProfileApiI = {};

    readonly additionalTimeTypes = ADDITIONAL_TIME_TYPES;
    readonly additionalTimeUnits = ADDITIONAL_TIME_UNITS;
    readonly additionalTimeUnitMax = ADDITIONAL_TIME_UNIT_MAX;
    readonly additionalTimeLabel = additionalTimeLabel;

    /** Stable ids so required hints stay associated with their controls. */
    readonly attendeesRequiredHintId = "My25Live_EventCreation_Attendees_Required";
    readonly searchWithinLabelId = "My25Live_EventCreation_Collections_Label";

    readonly findLocationLang = s25Resource(
        async () => (await DashLangService.getLang()).panel?.available_space?.find_location as FindLocationLang,
    );

    readonly initResource = s25Resource(async () => {
        const [defaultTime, fls, groupId, standardScheduleList] = await Promise.all([
            DatetimeService.nextHour(),
            FlsService.getFls(),
            UserprefService.getGroupId(),
            StandardScheduleService.getStandardScheduleList(4),
        ]);
        const startDatetime = S25Util.date.clone(defaultTime);
        let endDatetime = S25Util.date.clone(defaultTime);
        if (startDatetime.getHours() < 23) {
            endDatetime = S25Util.date.addHours(endDatetime, 1);
        } else {
            endDatetime.setMinutes(59);
        }
        const hasDateTemplate = [AccessLevels.Full, AccessLevels.Edit, AccessLevels.Read].includes(fls.ML_DATE);
        const hasDateTemplateSave = fls.ML_DATE === AccessLevels.Full;
        const canCreateStandardSchedules = fls.M25_EXPORT !== AccessLevels.None || groupId === -1;
        const hasConfiguredStandardSchedules = S25Util.array.forceArray(standardScheduleList).length > 0;
        const showMeetingPatternOption = hasConfiguredStandardSchedules || canCreateStandardSchedules;
        return { startDatetime, endDatetime, hasDateTemplate, hasDateTemplateSave, showMeetingPatternOption };
    });

    readonly dateTemplatesResource = s25Resource({
        if: () => this.hasDateTemplate(),
        loader: () => DateTemplateService.getTemplates(),
    });

    readonly dateTimeFormat = s25Resource(() => UserprefService.getS25DateTimeformat());

    readonly locationSearchCatalogResource = s25Resource(() =>
        SearchService.getStrictSearches(Item.Ids.Location, false),
    );

    readonly locationSearchPlaceholder = computed(
        () => this.findLocationLang.value()?.text.select_your_location_collection ?? "",
    );

    readonly searchWithinAriaLabel = computed(() => {
        const text = this.findLocationLang.value()?.text;
        if (!text?.search_within) {
            return "";
        }
        return text.required ? `${text.search_within} ${text.required}` : text.search_within;
    });

    readonly additionalLocationSearches = computed((): DropDownItem[] => {
        const catalog = this.locationSearchCatalogResource.value();
        const searches = catalog?.opt ?? [];
        if (searches.length) {
            return [];
        }
        const label = this.findLocationLang.value()?.text.all_locations ?? "All locations";
        return [{ itemName: label, txt: label, itemId: undefined }];
    });

    readonly dateMode = signal<"simple" | "template" | "meetingPattern">("simple");
    readonly meetingPatternApiBean: DashMeetingPatternApiBean = {};
    readonly meetingPatternAlert =
        "Please select a standard schedule, meeting pattern, and valid term start and end dates.";
    readonly showExtraFields = signal(false);
    readonly additionalTimeExpanded = signal(false);
    readonly timeModel = signal<TimeModelI | undefined>(undefined);
    readonly profileCode = signal("dnr");
    readonly adHocOccs = signal<Occurrence[]>([]);
    readonly templateOccurrences = signal<Occurrence[]>([]);
    readonly selectedDateTemplateId = signal<number | undefined>(undefined);
    readonly profileName = signal("");
    readonly dateTemplateSaving = signal(false);
    // Past-date warning: only surfaced once the user has actually picked/changed a date, and
    // cleared once they acknowledge it (mirrors the Express Scheduling behavior / legacy ANG-4673).
    readonly pastDateInteracted = signal(false);
    readonly pastDateOverride = signal(false);
    // Bumped to force the (init-only) datetime picker and template dropdown to re-read
    // their inputs after a template is applied or the form is reset.
    readonly datetimeRemountKey = signal(0);
    readonly dateTemplateDropdownRemountKey = signal(0);
    readonly numberOfAttendees = signal("");
    readonly selectedLocationSearch = signal<DropDownItem | undefined>(undefined);
    readonly showResults = signal(false);
    readonly resultsQuery = signal<FindLocationResultsQuery | undefined>(undefined);
    readonly resultsCapacity = signal<{ min: number; max: number } | undefined>(undefined);
    readonly resultsRevision = signal(0);

    readonly hasDateTemplate = computed(() => this.initResource.value()?.hasDateTemplate ?? false);
    readonly hasDateTemplateSave = computed(() => this.initResource.value()?.hasDateTemplateSave ?? false);
    readonly showMeetingPatternOption = computed(() => this.initResource.value()?.showMeetingPatternOption ?? false);
    readonly showDateModeToggle = computed(() => this.showMeetingPatternOption());
    readonly useDateTemplate = computed(() => this.dateMode() === "template");
    readonly useMeetingPattern = computed(() => this.dateMode() === "meetingPattern");
    readonly dateModeToggleValue = computed<boolean | undefined>(() => {
        const mode = this.dateMode();
        return mode === "simple" ? undefined : mode === "meetingPattern";
    });
    readonly showTemplateExtras = computed(() => {
        if (!this.useDateTemplate() || !this.showExtraFields()) {
            return undefined;
        }
        return this.timeModel();
    });
    readonly datetimeReady = computed(
        () => !!this.initResource.value() && !!this.startDatetime() && !!this.endDatetime(),
    );

    readonly pastDateWarning = computed(() => {
        if (this.dateMode() === "meetingPattern" || this.pastDateOverride() || !this.pastDateInteracted()) {
            return "";
        }
        const startDatetime = this.startDatetime();
        if (!startDatetime) {
            return "";
        }
        return startDatetime.getTime() < Date.now()
            ? "Warning: You are creating an event that begins in the past."
            : "";
    });

    readonly dateTemplateDropdownItems = computed(() =>
        (this.dateTemplatesResource.value() ?? []).map((template) => ({
            itemId: template.profile_id,
            itemName: template.profile_name,
        })),
    );

    readonly selectedDateTemplateItem = computed(() => {
        const selectedId = this.selectedDateTemplateId();
        return this.dateTemplateDropdownItems().find((item) => item.itemId === selectedId);
    });

    readonly startDatetime = linkedSignal(() => this.initResource.value()?.startDatetime);
    readonly endDatetime = linkedSignal(() => this.initResource.value()?.endDatetime);

    readonly reservationStart = computed(() => {
        const timeModel = this.timeModel();
        const startDatetime = this.startDatetime();
        if (!timeModel?.minutes || !startDatetime) {
            return undefined;
        }
        return EventFormOccurrenceUtil.getStartDtFromDtMin(startDatetime, timeModel.minutes);
    });

    readonly reservationEnd = computed(() => {
        const timeModel = this.timeModel();
        const endDatetime = this.endDatetime();
        if (!timeModel?.minutes || !endDatetime) {
            return undefined;
        }
        return EventFormOccurrenceUtil.getEndDtFromDtMin(endDatetime, timeModel.minutes);
    });

    readonly reservationStartLabel = computed(() => {
        const reservationStart = this.reservationStart();
        const dateTimeFormat = this.dateTimeFormat.value();
        if (!reservationStart || !dateTimeFormat) {
            return "";
        }
        return S25Datefilter.transform(reservationStart, dateTimeFormat);
    });

    readonly reservationEndLabel = computed(() => {
        const reservationEnd = this.reservationEnd();
        const dateTimeFormat = this.dateTimeFormat.value();
        if (!reservationEnd || !dateTimeFormat) {
            return "";
        }
        return S25Datefilter.transform(reservationEnd, dateTimeFormat);
    });

    readonly reservationDuration = computed(() => {
        const reservationStart = this.reservationStart();
        const reservationEnd = this.reservationEnd();
        if (!reservationStart || !reservationEnd) {
            return "";
        }
        return S25Util.getDurationString(S25Util.date.diffMinutes(reservationStart, reservationEnd));
    });

    readonly eventDuration = computed(() => {
        const startDatetime = this.startDatetime();
        const endDatetime = this.endDatetime();
        if (!startDatetime || !endDatetime) {
            return "";
        }
        return S25Util.getDurationString(S25Util.date.diffMinutes(startDatetime, endDatetime));
    });

    readonly resultsContext = computed(() => this.findLocationLang.value()?.space_avail_results);

    readonly availabilityResource = s25Resource({
        if: () => this.showResults() && !!this.resultsQuery() && !!this.resultsCapacity(),
        params: () => {
            const query = this.resultsQuery();
            const dates = (query?.getDates(true) ?? []) as FindLocationOccurrence[];
            return {
                queryId: query?.queryId,
                capacity: this.resultsCapacity()!,
                dateWindows: dates.map((dateRange) => ({
                    startDt: S25Util.date.toS25ISODateTimeStr(S25Util.date.clone(dateRange.startDt)) ?? "",
                    endDt: S25Util.date.toS25ISODateTimeStr(S25Util.date.clone(dateRange.endDt)) ?? "",
                })),
                revision: this.resultsRevision(),
            };
        },
        loader: async ({ params }) => {
            if (!params?.capacity) {
                return { items: [] as SpaceAvailabilityItem[], hasPrevCapacity: false, hasNextCapacity: false };
            }
            const [hasPrevCapacity, hasNextCapacity, items] = await Promise.all([
                DashPanelService.isPrevCapacity(params.capacity.min),
                DashPanelService.isNextCapacity(params.capacity.max),
                SpaceService.getSpacesAvailability(params.queryId ?? 0, params.capacity, params.dateWindows),
            ]);
            return {
                items: (items ?? []) as SpaceAvailabilityItem[],
                hasPrevCapacity,
                hasNextCapacity,
            };
        },
    });

    readonly availabilityItems = computed(() => this.availabilityResource.value()?.items ?? []);
    readonly availabilityLoaded = computed(
        () => this.availabilityResource.resource.hasValue() && !this.availabilityResource.isLoading(),
    );
    readonly hasPrevCapacity = computed(() => this.availabilityResource.value()?.hasPrevCapacity ?? false);
    readonly hasNextCapacity = computed(() => this.availabilityResource.value()?.hasNextCapacity ?? false);
    readonly resultsDescriptionHtml = computed(() => {
        const resultsLang = this.resultsContext();
        const capacity = this.resultsCapacity();
        if (!resultsLang || !capacity) {
            return "";
        }
        return resultsLang.desc.text.locationResults(capacity, this.availabilityItems().length);
    });

    constructor() {
        effect(() => {
            if (this.locationSearchCatalogResource.isLoading()) {
                return;
            }
            const catalog = this.locationSearchCatalogResource.value();
            const searches = catalog?.opt ?? [];
            if (!searches.length && !this.selectedLocationSearch()) {
                this.selectedLocationSearch.set(this.additionalLocationSearches()[0]);
            }
        });

        effect(() => {
            const init = this.initResource.value();
            if (init && !this.timeModel()) {
                const model = createDefaultTimeModel(init.startDatetime, init.endDatetime);
                refreshAdditionalTimeFieldsFromMinutes(model);
                this.timeModel.set(model);
            }
        });

        effect(() => {
            if (this.useDateTemplate()) {
                return;
            }
            this.showExtraFields.set(false);
        });

        effect(() => {
            this.startDatetime();
            this.endDatetime();
            this.numberOfAttendees();
            this.selectedLocationSearch();
            this.dateMode();
            this.showExtraFields();
            this.selectedDateTemplateId();
            this.templateOccurrences();
            this.profileCode();
            this.timeModel();

            untracked(() => {
                if (this.showResults()) {
                    this.showResults.set(false);
                }
            });
        });

        this.wireDatePatternApi();
    }

    onDateModeToggleChange(value: boolean | undefined): void {
        const mode = value === undefined ? "simple" : value ? "meetingPattern" : "template";
        this.dateMode.set(mode);
        if (mode === "template") {
            this.ensureTemplateOccurrencesSeeded();
            TelemetryService.send("DateTemplates", "Choose");
            TelemetryService.sendWithSub("DashFindLocation", "dateTemplate", "Choose");
        } else if (mode === "meetingPattern") {
            TelemetryService.sendWithSub("DashFindLocation", "meetingPattern", "Choose");
        }
    }

    resetDateMode(): void {
        this.meetingPatternApiBean.reset?.();
        this.dateMode.set("simple");
        this.pastDateInteracted.set(false);
        this.pastDateOverride.set(false);
        this.clearDateTemplateApplication();
    }

    ignorePastDateWarning(): void {
        this.pastDateOverride.set(true);
    }

    additionalTimeField(timeModel: TimeModelI, timeType: AdditionalTimeType, unit: AdditionalTimeUnit): number {
        return timeModel[`_${timeType}${unit}`] ?? 0;
    }

    onShowExtraFieldsChange(showExtraFields: boolean): void {
        this.showExtraFields.set(showExtraFields);
        if (showExtraFields) {
            this.ensureTemplateOccurrencesSeeded();
        }
    }

    onCalendarOccurrencesChange(occurrences: Occurrence[]): void {
        const profileCode = this.profileCode();
        if (!["adhoc", "dnr"].includes(profileCode) && occurrences.length) {
            this.profileCode.set("adhoc");
            this.adHocOccs.set(occurrences);
        } else if (profileCode === "adhoc") {
            this.adHocOccs.set(occurrences);
        }
        this.templateOccurrences.set(occurrences);
    }

    onAdditionalTimeFieldChange(timeType: AdditionalTimeType, unit: AdditionalTimeUnit, value: number): void {
        this.timeModel.update((timeModel) => {
            if (!timeModel) {
                return timeModel;
            }
            timeModel[`_${timeType}${unit}`] = Math.max(0, value ?? 0);
            syncAdditionalTimeMinutesFromFields(timeModel);
            return { ...timeModel, minutes: { ...timeModel.minutes! } };
        });
    }

    openRepeatingPatternModal(): void {
        const timeModel = this.timeModel();
        if (!timeModel) {
            return;
        }
        const patternApiBean: ProfileApiI = {};
        const formModel = this.buildTemplateFormModel();
        void ModalService.byClass(EventFormDatePatternPickerModal, {
            profileCode: this.profileCode(),
            formModel,
            timeModel,
            apiBean: patternApiBean,
        }).then((result: { confirmed?: boolean } | undefined) => {
            if (!result?.confirmed) {
                return;
            }
            void OccurrenceProfileUtil.replacePattern(formModel, false, patternApiBean.getProfileCode?.()).then(() =>
                this.applyFormModelOccurrences(formModel),
            );
        });
    }

    onStartDatetimeChange(startDatetime: Date): void {
        const clonedStart = S25Util.date.clone(startDatetime);
        this.startDatetime.set(clonedStart);
        this.pastDateInteracted.set(true);
        this.pastDateOverride.set(false);
        this.timeModel.update((timeModel) => {
            if (!timeModel) {
                return timeModel;
            }
            timeModel.evStartDt = S25Util.date.clone(clonedStart);
            return { ...timeModel };
        });
        if (this.useDateTemplate()) {
            this.syncOccurrencesOnFirstDateChange(clonedStart);
        }
    }

    onEndDatetimeChange(endDatetime: Date): void {
        const clonedEnd = S25Util.date.clone(endDatetime);
        this.endDatetime.set(clonedEnd);
        this.pastDateInteracted.set(true);
        this.timeModel.update((timeModel) => {
            if (!timeModel) {
                return timeModel;
            }
            timeModel.evEndDt = S25Util.date.clone(clonedEnd);
            return { ...timeModel };
        });
    }

    onDateTemplateSelect(selected: DropDownItem | number | null | undefined): void {
        const templateId =
            typeof selected === "object" && selected ? (selected.itemId as number) : (selected ?? undefined);
        this.selectedDateTemplateId.set(templateId as number | undefined);
        const template = this.dateTemplatesResource.value()?.find((candidate) => candidate.profile_id === templateId);
        if (template) {
            this.profileName.set(template.profile_name);
            this.applySelectedDateTemplate(template);
            this.pastDateInteracted.set(true);
            this.pastDateOverride.set(false);
        }
    }

    resetDateTemplate(): void {
        this.clearDateTemplateApplication();
        this.showExtraFields.set(true);
        this.pastDateInteracted.set(false);
        this.pastDateOverride.set(false);
        const init = this.initResource.value();
        if (init) {
            this.applyDatetimesToState(init.startDatetime, init.endDatetime);
        }
        this.ensureTemplateOccurrencesSeeded();
    }

    saveDateTemplate(saveAs: boolean): void {
        void this.runDateTemplateSave(saveAs);
    }

    deleteDateTemplate(): void {
        void this.runDateTemplateDelete();
    }

    onLocationSearchChange(locationSearch: DropDownItem | null | undefined): void {
        this.selectedLocationSearch.set(locationSearch ?? undefined);
    }

    async showLocations(): Promise<void> {
        const context = this.findLocationLang.value();
        if (!context) {
            return;
        }

        const parsedAttendees = this.parseNumberOfAttendees(this.numberOfAttendees());
        if (parsedAttendees === undefined) {
            window.alert(context.text.attendees_alert);
            return;
        }
        if (S25Util.isUndefined(this.selectedLocationSearch())) {
            window.alert(context.text.location_collection_alert);
            return;
        }
        if (this.useMeetingPattern() && !this.meetingPatternApiBean.isValid?.()) {
            window.alert(this.meetingPatternAlert);
            return;
        }

        const capacity = await DashPanelService.getCapacity(parsedAttendees);
        const selectedLocationSearch = this.selectedLocationSearch()!;
        let searchDates = S25Util.deepCopy(this.buildOccurrences(true)) as FindLocationOccurrence[];
        let searchOccurrences = S25Util.deepCopy(this.buildOccurrences(false));
        if (this.useDateTemplate()) {
            searchOccurrences = this.stampPickerTimesOnOccurrences(searchOccurrences as Occurrence[]);
            searchDates = (searchOccurrences as Occurrence[]).map((occurrence) => ({
                startDt: occurrence.evStartDt,
                endDt: occurrence.evEndDt,
            }));
        }
        // SpaceService checks the assignment policy against the first date, so it must be the earliest.
        searchDates.sort(S25Util.shallowSortDates("startDt", "endDt"));

        this.resultsQuery.set({
            queryId: selectedLocationSearch.itemId as number | undefined,
            numberOfAttendees: parsedAttendees,
            profileCode: this.resolveProfileCode(),
            getDates: (datesOnly) => (datesOnly ? searchDates : searchOccurrences),
        });
        this.resultsCapacity.set(capacity);
        this.resultsRevision.update((revision) => revision + 1);
        TelemetryService.sendWithSub("DashFindLocation", this.dateMode(), "Search");
        this.showResults.set(true);
    }

    private resolveProfileCode(): string | null {
        if (this.useMeetingPattern()) {
            return this.meetingPatternApiBean.getProfileCode?.() ?? null;
        }
        if (this.useDateTemplate()) {
            return this.resolveTemplateProfileCode();
        }
        return null;
    }

    viewLocation(spaceItem: SpaceAvailabilityItem): void {
        void StateService.gotoItem({
            itemId: spaceItem.itemId,
            itemTypeId: Item.Ids.Location,
            itemName: spaceItem.itemName,
        });
    }

    useSpace(spaceItem: SpaceAvailabilityItem): void {
        const query = this.resultsQuery();
        if (!query) {
            return;
        }
        void StateService.createEvent({
            itemId: spaceItem.itemId,
            itemTypeId: Item.Ids.Location,
            headCount: query.numberOfAttendees,
            occurrences: query.getDates(false),
            profileCode: query.profileCode,
        });
    }

    advancedSearch(): void {
        const query = this.resultsQuery();
        const dates = (query?.getDates(true) ?? []) as FindLocationOccurrence[];
        const startDt = dates[0]?.startDt ?? new Date();
        void StateService.gotoItem({
            itemTypeId: 3004,
            startDt,
        } as Parameters<typeof StateService.gotoItem>[0] & { startDt?: Date });
    }

    async loadPrevCapacity(): Promise<void> {
        const capacity = this.resultsCapacity();
        if (!capacity) {
            return;
        }
        this.resultsCapacity.set(await DashPanelService.getCapacity(capacity.min - 1));
        this.resultsRevision.update((revision) => revision + 1);
    }

    async loadNextCapacity(): Promise<void> {
        const capacity = this.resultsCapacity();
        if (!capacity) {
            return;
        }
        this.resultsCapacity.set(await DashPanelService.getCapacity(capacity.max + 1));
        this.resultsRevision.update((revision) => revision + 1);
    }

    private parseNumberOfAttendees(rawValue: string): number | undefined {
        if (!S25Util.isParsableAsInt(rawValue)) {
            return undefined;
        }
        const parsed = S25Util.toInt(rawValue);
        return parsed >= 0 ? parsed : undefined;
    }

    private buildOccurrences(datesOnly: boolean): FindLocationOccurrence[] | Occurrence[] {
        if (this.useMeetingPattern()) {
            return datesOnly
                ? (this.meetingPatternApiBean.getStartEndDates?.() ?? [])
                : (this.meetingPatternApiBean.getOccurrences?.() ?? []);
        }
        if (this.useDateTemplate()) {
            const occurrences = this.resolveTemplateOccurrences();
            if (datesOnly) {
                return occurrences.map((occurrence) => ({
                    startDt: occurrence.evStartDt,
                    endDt: occurrence.evEndDt,
                }));
            }
            return occurrences;
        }
        const startDatetime = this.startDatetime();
        const endDatetime = this.endDatetime();
        if (!startDatetime || !endDatetime) {
            return [];
        }
        return [{ startDt: startDatetime, endDt: endDatetime }];
    }

    private clearDateTemplateApplication(): void {
        this.selectedDateTemplateId.set(undefined);
        this.dateTemplateDropdownRemountKey.update((key) => key + 1);
        this.profileName.set("");
        this.profileCode.set("dnr");
        this.adHocOccs.set([]);
        this.templateOccurrences.set([]);
    }

    private applyDatetimesToState(startDatetime: Date, endDatetime: Date): void {
        this.startDatetime.set(S25Util.date.clone(startDatetime));
        this.endDatetime.set(S25Util.date.clone(endDatetime));
        // The editable datetime picker only reads its inputs on init, so remount it to show the new dates.
        this.datetimeRemountKey.update((key) => key + 1);
        this.timeModel.update((timeModel) => {
            if (!timeModel) {
                return timeModel;
            }
            return {
                ...timeModel,
                evStartDt: S25Util.date.clone(startDatetime),
                evEndDt: S25Util.date.clone(endDatetime),
            };
        });
    }

    private applySelectedDateTemplate(template: DateTemplateRecord): void {
        const applied = parseDateTemplate(template);
        this.applyDatetimesToState(applied.startDatetime, applied.endDatetime);

        this.timeModel.update((timeModel) => {
            if (!timeModel) {
                return timeModel;
            }
            timeModel.minutes = {
                setupMinutes: applied.setupMinutes,
                preMinutes: 0,
                postMinutes: 0,
                takedownMinutes: applied.takedownMinutes,
            };
            refreshAdditionalTimeFieldsFromMinutes(timeModel);
            return { ...timeModel, minutes: { ...timeModel.minutes } };
        });

        this.profileCode.set(applied.profileCode);
        if (applied.profileCode === "adhoc") {
            const minutes = this.timeModel()?.minutes;
            this.adHocOccs.set(applied.adHocOccurrences.map((occurrence) => ({ ...occurrence, minutes })));
            this.templateOccurrences.set(this.adHocOccs());
        } else if (applied.profileCode !== "dnr") {
            this.adHocOccs.set([]);
            const timeModel = this.timeModel();
            if (timeModel) {
                this.templateOccurrences.set(syncTemplateOccurrencesFromProfileCode(timeModel, applied.profileCode));
            }
        } else {
            this.adHocOccs.set([]);
            this.templateOccurrences.set(
                seedTemplateOccurrence(applied.startDatetime, applied.endDatetime, this.timeModel()?.minutes),
            );
        }
    }

    private wireDatePatternApi(): void {
        this.datePatternApi.getProfileCode = () => this.profileCode();
        this.datePatternApi.getOccurrences = () => this.resolveTemplateOccurrences();
        this.datePatternApi.getProfileModel = () => this.getProfileModelForApi();
    }

    private getProfileModelForApi(): ProfileI {
        const profileCode = this.profileCode();
        if (profileCode === "adhoc" || profileCode === "dnr") {
            return { type: profileCode };
        }
        const timeModel = this.timeModel();
        if (!timeModel) {
            return { type: "dnr" };
        }
        const throughDate = resolveThroughDate(timeModel, profileCode);
        return ProfileUtil.getProfileModel(null, profileCode, throughDate, timeModel.evStartDt);
    }

    private buildTemplateFormModel(): FormModel & { respectMinMax: boolean; occurrences: Occurrence[] } {
        const timeModel = this.timeModel()!;
        const formModel: FormModel & { respectMinMax: boolean; occurrences: Occurrence[] } = {
            respectMinMax: true,
            timeModel,
            profileCode: this.profileCode(),
            occurrences: [...this.resolveTemplateOccurrences()],
            locations: [],
            resources: [],
            minCreateDate: new Date(2000, 0, 1),
            maxCreateDate: new Date(2100, 11, 31),
            clickToPickApiBean: {
                totalCalendarRefresh: () => this.onCalendarRefresh(),
            },
            updateProfileCode: (newProfileCode: string) => {
                formModel.profileCode = newProfileCode;
                this.profileCode.set(newProfileCode);
            },
            removeOccurrence: (occurrence: Occurrence) => {
                const index = formModel.occurrences.indexOf(occurrence);
                if (index >= 0) {
                    formModel.occurrences.splice(index, 1);
                }
            },
            addOccurrence: (
                evStartDt: Date,
                evEndDt: Date,
                minutes: Occurrence["minutes"],
                _ifNotExists: boolean,
                newProfileCode: string,
            ) => {
                formModel.occurrences.push({
                    evStartDt,
                    evEndDt,
                    minutes: minutes ?? timeModel.minutes,
                });
                formModel.profileCode = newProfileCode;
                this.profileCode.set(newProfileCode);
            },
        };
        return formModel;
    }

    private applyFormModelOccurrences(formModel: FormModel & { occurrences: Occurrence[] }): void {
        const occurrences = [...(formModel.occurrences ?? [])];
        this.templateOccurrences.set(occurrences);
        this.adHocOccs.set(formModel.profileCode === "adhoc" ? occurrences : []);
        if (formModel.profileCode) {
            this.profileCode.set(formModel.profileCode);
        }
    }

    private onCalendarRefresh(): void {
        const profileCode = this.profileCode();
        const timeModel = this.timeModel();
        if (timeModel && profileCode && !["dnr", "adhoc"].includes(profileCode)) {
            this.templateOccurrences.set(syncTemplateOccurrencesFromProfileCode(timeModel, profileCode));
        }
    }

    private ensureTemplateOccurrencesSeeded(): void {
        if (this.templateOccurrences().length) {
            return;
        }
        const timeModel = this.timeModel();
        const startDatetime = this.startDatetime();
        const endDatetime = this.endDatetime();
        if (!timeModel || !startDatetime || !endDatetime) {
            return;
        }
        this.templateOccurrences.set(seedTemplateOccurrence(startDatetime, endDatetime, timeModel.minutes));
    }

    private syncOccurrencesOnFirstDateChange(newStartDatetime: Date): void {
        const profileCode = this.profileCode();
        const endDatetime = this.endDatetime();
        const minutes = this.timeModel()?.minutes;
        if (!endDatetime) {
            return;
        }

        if (profileCode === "adhoc") {
            const filteredOccurrences = this.templateOccurrences().filter(
                (occurrence) => occurrence.evStartDt >= newStartDatetime,
            );
            const occurrences = filteredOccurrences.length
                ? filteredOccurrences
                : seedTemplateOccurrence(newStartDatetime, endDatetime, minutes);
            this.adHocOccs.set(occurrences);
            this.templateOccurrences.set(occurrences);
            return;
        }

        const timeModel = this.timeModel();
        if (profileCode && profileCode !== "dnr" && timeModel) {
            this.templateOccurrences.set(syncTemplateOccurrencesFromProfileCode(timeModel, profileCode));
            return;
        }

        this.templateOccurrences.set(seedTemplateOccurrence(newStartDatetime, endDatetime, minutes));
    }

    private stampPickerTimesOnOccurrences(occurrences: Occurrence[]): Occurrence[] {
        const pickerStart = this.startDatetime();
        const pickerEnd = this.endDatetime();
        if (!pickerStart || !pickerEnd || !occurrences.length) {
            return occurrences;
        }
        const daySpan = S25Util.date.diffDays(pickerStart, pickerEnd);
        return occurrences.map((occurrence) => {
            const evStartDt = S25Util.date.copyHourMinute(occurrence.evStartDt, pickerStart);
            return {
                ...occurrence,
                evStartDt,
                evEndDt: S25Util.date.copyHourMinute(S25Util.date.addDays(evStartDt, daySpan), pickerEnd),
            };
        });
    }

    private applyPickerTimesToTemplateOccurrences(): Occurrence[] {
        const stampedOccurrences = this.stampPickerTimesOnOccurrences(this.resolveTemplateOccurrences());
        this.templateOccurrences.set(stampedOccurrences);
        if (this.profileCode() === "adhoc") {
            this.adHocOccs.set(stampedOccurrences);
        }
        return stampedOccurrences;
    }

    private resolveTemplateProfileCode(): string | null {
        return this.datePatternApi.getProfileCode?.() ?? this.profileCode() ?? null;
    }

    private resolveTemplateOccurrences(): Occurrence[] {
        if (this.templateOccurrences().length) {
            return this.templateOccurrences();
        }
        const startDatetime = this.startDatetime();
        const endDatetime = this.endDatetime();
        const minutes = this.timeModel()?.minutes;
        if (startDatetime && endDatetime) {
            return [{ evStartDt: startDatetime, evEndDt: endDatetime, minutes }];
        }
        return [];
    }

    private getDateTemplateRecTypeId(): number {
        const occurrences = this.datePatternApi.getOccurrences?.() ?? [];
        const profileCode = this.datePatternApi.getProfileCode?.() ?? "";
        if (occurrences.length <= 1) {
            return 0;
        }
        if (["dnr", "adhoc"].includes(profileCode)) {
            return 1;
        }
        return 2;
    }

    private async runDateTemplateSave(saveAs: boolean): Promise<void> {
        const profileName = this.profileName().trim();
        const timeModel = this.timeModel();
        const initStartDt = this.startDatetime() ?? timeModel?.evStartDt;
        const initEndDt = this.endDatetime() ?? timeModel?.evEndDt;
        let profileCode = this.datePatternApi.getProfileCode?.() ?? "";
        profileCode = ["adhoc", "dnr"].includes(profileCode) ? "" : profileCode;

        if (!profileName) {
            window.alert("Please enter a profile name.");
            return;
        }
        if (!initStartDt || !initEndDt) {
            window.alert("Please choose initial dates.");
            return;
        }

        const occurrences = this.applyPickerTimesToTemplateOccurrences();
        if (!occurrences.length) {
            window.alert("Please select occurrences.");
            return;
        }

        const profileId = !saveAs && this.selectedDateTemplateId() ? this.selectedDateTemplateId()! : null;

        this.dateTemplateSaving.set(true);
        try {
            TelemetryService.send("DateTemplates", "Save");
            const savedProfileId = await DateTemplateService.saveTemplate(
                profileId,
                profileName,
                profileCode,
                this.getDateTemplateRecTypeId(),
                occurrences,
                initStartDt,
                initEndDt,
            );
            await this.dateTemplatesResource.reloadAndAwait();
            this.selectedDateTemplateId.set(savedProfileId);
            const savedTemplate = this.dateTemplatesResource
                .value()
                ?.find((template) => template.profile_id === savedProfileId);
            if (savedTemplate) {
                this.profileName.set(savedTemplate.profile_name);
                this.applySelectedDateTemplate(savedTemplate);
            }
        } catch (caughtError) {
            S25Util.showError(caughtError);
        } finally {
            this.dateTemplateSaving.set(false);
        }
    }

    private async runDateTemplateDelete(): Promise<void> {
        const profileId = this.selectedDateTemplateId();
        if (!profileId) {
            window.alert("Please select a template to delete.");
            return;
        }

        this.dateTemplateSaving.set(true);
        try {
            await DateTemplateService.deleteTemplate(profileId);
            this.resetDateTemplate();
            await this.dateTemplatesResource.reloadAndAwait();
        } catch (caughtError) {
            S25Util.showError(caughtError);
        } finally {
            this.dateTemplateSaving.set(false);
        }
    }
}
